import JSZip from 'jszip';

const TEXT_NS = 'urn:oasis:names:tc:opendocument:xmlns:text:1.0';
const OFFICE_NS = 'urn:oasis:names:tc:opendocument:xmlns:office:1.0';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function processTextNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || '');
  }
  return '';
}

function processTextContent(element: Element): string {
  let result = '';
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      result += processTextNode(child);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const childElement = child as Element;
      const localName = childElement.localName;
      const namespace = childElement.namespaceURI;

      if (namespace === TEXT_NS) {
        switch (localName) {
          case 'span':
            const spanContent = processTextContent(childElement);
            const styleName = childElement.getAttribute('text:style-name') || '';
            if (styleName.includes('bold') || styleName.includes('Strong')) {
              result += `<strong>${spanContent}</strong>`;
            } else if (styleName.includes('italic') || styleName.includes('Emphasis')) {
              result += `<em>${spanContent}</em>`;
            } else {
              result += spanContent;
            }
            break;
          case 's':
            result += `<span style="text-decoration: line-through;">${processTextContent(childElement)}</span>`;
            break;
          case 'a':
            const href = childElement.getAttributeNS(XLINK_NS, 'href') || childElement.getAttribute('href') || '';
            result += `<a href="${escapeHtml(href)}">${processTextContent(childElement)}</a>`;
            break;
          case 'line-break':
            result += '<br>';
            break;
          default:
            result += processTextContent(childElement);
            break;
        }
      } else {
        result += processTextContent(childElement);
      }
    }
  }
  return result;
}

function processElement(element: Element): string {
  const localName = element.localName;
  const namespace = element.namespaceURI;

  if (namespace === TEXT_NS) {
    switch (localName) {
      case 'p':
        const pContent = processTextContent(element);
        return pContent.trim() ? `<p>${pContent}</p>` : '<p><br></p>';
      case 'h':
        const outlineLevel = element.getAttribute('text:outline-level') || 
                            element.getAttributeNS(TEXT_NS, 'outline-level') ||
                            '1';
        const level = Math.min(Math.max(parseInt(outlineLevel, 10), 1), 6);
        const hContent = processTextContent(element);
        if (!hContent.trim()) {
          return '';
        }
        return `<h${level}>${hContent}</h${level}>`;
      case 'list':
        return processList(element);
      case 'table':
        return processTable(element);
      default:
        return processTextContent(element);
    }
  }

  if (namespace === OFFICE_NS && localName === 'text') {
    let result = '';
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        result += processElement(child as Element);
      }
    }
    return result;
  }

  let result = '';
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      result += processElement(child as Element);
    }
  }
  return result;
}

function processList(listElement: Element): string {
  let result = '<ul>';
  const listItems = listElement.getElementsByTagNameNS(TEXT_NS, 'list-item');
  for (const item of Array.from(listItems)) {
    let itemContent = '';
    for (const child of Array.from(item.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        itemContent += processElement(child as Element);
      }
    }
    if (itemContent.trim()) {
      result += `<li>${itemContent}</li>`;
    }
  }
  result += '</ul>';
  return result;
}

function processTable(tableElement: Element): string {
  let result = '<table>';
  const rows = tableElement.getElementsByTagNameNS('urn:oasis:names:tc:opendocument:xmlns:table:1.0', 'table-row');
  for (const row of Array.from(rows)) {
    result += '<tr>';
    const cells = row.getElementsByTagNameNS('urn:oasis:names:tc:opendocument:xmlns:table:1.0', 'table-cell');
    for (const cell of Array.from(cells)) {
      let cellContent = '';
      for (const child of Array.from(cell.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          cellContent += processElement(child as Element);
        }
      }
      result += `<td>${cellContent}</td>`;
    }
    result += '</tr>';
  }
  result += '</table>';
  return result;
}

function getLocalName(element: Element): string {
  return element.localName || element.tagName.split(':').pop() || element.tagName;
}

function isHeading(element: Element): boolean {
  const localName = getLocalName(element);
  const tagName = element.tagName;
  return localName === 'h' || tagName === 'text:h' || tagName.endsWith(':h');
}

function isParagraph(element: Element): boolean {
  const localName = getLocalName(element);
  const tagName = element.tagName;
  return localName === 'p' || tagName === 'text:p' || tagName.endsWith(':p');
}

function isList(element: Element): boolean {
  const localName = getLocalName(element);
  const tagName = element.tagName;
  return localName === 'list' || tagName === 'text:list' || tagName.endsWith(':list');
}

function isTable(element: Element): boolean {
  const localName = getLocalName(element);
  const tagName = element.tagName;
  return localName === 'table' || tagName === 'table:table' || tagName.endsWith(':table');
}

function isTextNamespace(element: Element): boolean {
  const namespace = element.namespaceURI;
  const tagName = element.tagName;
  return namespace === TEXT_NS || tagName.startsWith('text:');
}

function isOfficeText(element: Element): boolean {
  const namespace = element.namespaceURI;
  const localName = getLocalName(element);
  const tagName = element.tagName;
  return (namespace === OFFICE_NS && localName === 'text') || tagName === 'office:text';
}

export async function convertOdtToHtml(arrayBuffer: ArrayBuffer): Promise<string> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const contentXml = await zip.file('content.xml')?.async('text');

  if (!contentXml) {
    throw new Error('Could not find content.xml in ODT file');
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(contentXml, 'text/xml');

  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Failed to parse ODT content.xml');
  }

  let body: Element | null = null;
  const bodyByNS = xmlDoc.getElementsByTagNameNS(OFFICE_NS, 'body');
  if (bodyByNS.length > 0) {
    body = bodyByNS[0];
  } else {
    const bodyByTag = xmlDoc.getElementsByTagName('office:body');
    if (bodyByTag.length > 0) {
      body = bodyByTag[0];
    }
  }

  if (!body) {
    throw new Error('Could not find body element in ODT file');
  }

  let textElement: Element | null = null;
  const textByNS = body.getElementsByTagNameNS(OFFICE_NS, 'text');
  if (textByNS.length > 0) {
    textElement = textByNS[0];
  } else {
    const textByTag = body.getElementsByTagName('office:text');
    if (textByTag.length > 0) {
      textElement = textByTag[0];
    }
  }

  if (!textElement) {
    const textContent = body.textContent || '';
    if (textContent.trim()) {
      return textContent
        .split('\n')
        .filter(line => line.trim())
        .map(line => `<p>${escapeHtml(line.trim())}</p>`)
        .join('');
    }
    return '<p></p>';
  }

  const orderedElements: Element[] = [];

  function traverseInOrder(element: Element, depth: number = 0): void {
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;

        if (isHeading(childElement) || isParagraph(childElement) || isList(childElement) || isTable(childElement)) {
          orderedElements.push(childElement);
        } else if (isOfficeText(childElement) || isTextNamespace(childElement)) {
          traverseInOrder(childElement, depth + 1);
        } else {
          traverseInOrder(childElement, depth + 1);
        }
      }
    }
  }

  traverseInOrder(textElement);

  if (orderedElements.length === 0) {
    const textContent = textElement.textContent || '';
    if (textContent.trim()) {
      return textContent
        .split('\n')
        .filter(line => line.trim())
        .map(line => `<p>${escapeHtml(line.trim())}</p>`)
        .join('');
    }
    return '<p></p>';
  }

  let html = '';
  for (const element of orderedElements) {
    html += processElementRobust(element);
  }

  return html || '<p></p>';
}

function getHeadingLevelFromStyle(styleName: string): number | null {
  if (!styleName) return null;

  const libreOfficeHeadingMatch = styleName.match(/Heading(?:_20_|[\s_])(\d)$/i);
  if (libreOfficeHeadingMatch) {
    return Math.min(Math.max(parseInt(libreOfficeHeadingMatch[1], 10), 1), 6);
  }

  const simpleHeadingMatch = styleName.match(/^Heading\s*(\d)$/i);
  if (simpleHeadingMatch) {
    return Math.min(Math.max(parseInt(simpleHeadingMatch[1], 10), 1), 6);
  }

  const h1Styles = ['Title', 'Titel', 'Título'];
  if (h1Styles.some(s => styleName === s || styleName.startsWith(s + '_') || styleName.endsWith('_' + s))) {
    return 1;
  }

  const subtitleStyles = ['Subtitle', 'Untertitel', 'Subtítulo'];
  if (subtitleStyles.some(s => styleName === s || styleName.startsWith(s + '_') || styleName.endsWith('_' + s))) {
    return 2;
  }

  return null;
}

function processElementRobust(element: Element): string {
  if (isParagraph(element)) {
    const styleName = element.getAttribute('text:style-name') || '';
    const headingLevel = getHeadingLevelFromStyle(styleName);

    if (headingLevel) {
      const hContent = processTextContent(element);
      if (!hContent.trim()) {
        return '';
      }
      return `<h${headingLevel}>${hContent}</h${headingLevel}>`;
    }

    const pContent = processTextContent(element);
    return pContent.trim() ? `<p>${pContent}</p>` : '<p><br></p>';
  }

  if (isHeading(element)) {
    const outlineLevel = element.getAttribute('text:outline-level') ||
                        element.getAttributeNS(TEXT_NS, 'outline-level') ||
                        '1';
    const level = Math.min(Math.max(parseInt(outlineLevel, 10), 1), 6);
    const hContent = processTextContent(element);
    if (!hContent.trim()) {
      return '';
    }
    return `<h${level}>${hContent}</h${level}>`;
  }

  if (isList(element)) {
    return processListRobust(element);
  }

  if (isTable(element)) {
    return processTable(element);
  }

  return processTextContent(element);
}

function processListRobust(listElement: Element): string {
  let result = '<ul>';
  const listItems = listElement.getElementsByTagName('text:list-item');
  const listItemsNS = listElement.getElementsByTagNameNS(TEXT_NS, 'list-item');
  const items = listItems.length > 0 ? listItems : listItemsNS;

  for (const item of Array.from(items)) {
    let itemContent = '';
    for (const child of Array.from(item.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        itemContent += processElementRobust(child as Element);
      }
    }
    if (itemContent.trim()) {
      result += `<li>${itemContent}</li>`;
    }
  }
  result += '</ul>';
  return result;
}

